import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Users, LogOut, Folder, Dumbbell, Settings,
  LayoutDashboard, UserCheck, BarChart2, ClipboardList,
  BookOpen, CreditCard, Shield, Activity, X,
  Calendar, Apple, MessageCircle, FileText, BookTemplate, Lock
} from "lucide-react";
import { applyBrandingCSS } from "../hooks/useCompanyBranding";
import { PLAN_FEATURES, MENU_ORDER, FEATURE_PLAN_MAP } from "../config/planConfig";
import UpgradeModal from "../components/UpgradeModal";

import DashboardTab       from "./bedrijf/DashboardTab";
import SportersTab        from "./bedrijf/SportersTab";
import MappenTab          from "./bedrijf/MappenTab";
import SchemasTab         from "./bedrijf/SchemasTab";
import InstellingenTab    from "./bedrijf/InstellingenTab";
import IntakeTab          from "./bedrijf/IntakeTab";
import PersoneelTab       from "./bedrijf/PersoneelTab";
import AnalyticsTab       from "./bedrijf/AnalyticsTab";
import AbonnementTab      from "./bedrijf/AbonnementTab";
import AdminsTab          from "./bedrijf/AdminsTab";
import TemplatesTab       from "./bedrijf/TemplatesTab";
import BiblioteekTab      from "./bedrijf/BiblioteekTab";
import ActiviteitTab      from "./bedrijf/ActiviteitTab";
import RoosterTab         from "./bedrijf/RoosterTab";
import CreditsTab         from "./bedrijf/CreditsTab";
import VoedingTab         from "./bedrijf/VoedingTab";
import CommunityTab       from "./bedrijf/CommunityTab";
import BedrijfFacturenTab from "./bedrijf/BedrijfFacturenTab";

const API = process.env.REACT_APP_BACKEND_URL;

// Alle mogelijke tabs (volledig overzicht)
const ALL_TABS = [
  { key: "dashboard",    label: "Dashboard",    icon: <LayoutDashboard size={14} /> },
  { key: "activiteit",   label: "Activiteit",   icon: <Activity size={14} /> },
  { key: "sporters",     label: "Sporters",     icon: <Users size={14} /> },
  { key: "intake",       label: "Intake",       icon: <ClipboardList size={14} /> },
  { key: "schemas",      label: "Schema's",     icon: <Dumbbell size={14} /> },
  { key: "voeding",      label: "Voeding",      icon: <Apple size={14} /> },
  { key: "rooster",      label: "Rooster",      icon: <Calendar size={14} /> },
  { key: "mappen",       label: "Mappen",       icon: <Folder size={14} /> },
  { key: "bibliotheek",  label: "Bibliotheek",  icon: <BookOpen size={14} /> },
  { key: "templates",    label: "Templates",    icon: <BookTemplate size={14} /> },
  { key: "credits",      label: "Credits",      icon: <CreditCard size={14} /> },
  { key: "abonnement",   label: "Abonnement",   icon: <CreditCard size={14} /> },
  { key: "personeel",    label: "Personeel",    icon: <UserCheck size={14} /> },
  { key: "beheerders",   label: "Beheerders",   icon: <Shield size={14} /> },
  { key: "community",    label: "Community",    icon: <MessageCircle size={14} /> },
  { key: "facturen",     label: "Facturen",     icon: <FileText size={14} /> },
  { key: "analytics",    label: "Analytics",    icon: <BarChart2 size={14} /> },
  { key: "instellingen", label: "Instellingen", icon: <Settings size={14} /> },
];

export default function BedrijfAdminPanel({ impersonateToken, onCloseImpersonate } = {}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState(null); // { feature, requiredPlan }

  const token = impersonateToken || user?.token || localStorage.getItem("fitsmith_token") || "";
  const isImpersonating = !!impersonateToken;

  useEffect(() => {
    if (isImpersonating) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "company_admin") { navigate("/account"); return; }
  }, [user, navigate, isImpersonating]);

  const loadCompany = useCallback(async () => {
    if (!token) return;
    setCompanyLoading(true);
    try {
      const r = await fetch(`${API}/api/company/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setCompanyInfo(data);
        if (data.branding) applyBrandingCSS(data.branding);
      }
    } catch {}
    finally { setCompanyLoading(false); }
  }, [token]);

  useEffect(() => { loadCompany(); }, [loadCompany]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Huidig plan en toegestane features
  const currentPlan = companyInfo?.plan || "basic";
  const allowedFeatures = PLAN_FEATURES[currentPlan] || PLAN_FEATURES.basic;

  // Filter en sorteer tabs op basis van plan + menu-volgorde
  const visibleTabs = MENU_ORDER
    .map(key => ALL_TABS.find(t => t.key === key))
    .filter(tab => tab && allowedFeatures.includes(tab.key));

  // Tab navigatie met upgrade-check
  const handleTabClick = (key) => {
    if (!allowedFeatures.includes(key)) {
      const requiredPlan = FEATURE_PLAN_MAP[key] || "pro";
      setUpgradeModal({ feature: key, requiredPlan });
      return;
    }
    setActiveTab(key);
  };

  // Als activeTab niet meer toegestaan is na plan-wijziging → reset
  useEffect(() => {
    if (!allowedFeatures.includes(activeTab)) {
      setActiveTab("dashboard");
    }
  }, [allowedFeatures, activeTab]);

  if (!isImpersonating && (!user || user.role !== "company_admin")) return null;

  const primaryColor = companyInfo?.branding?.primary_color || "#C9A84C";
  const bc = {
    bg:      companyInfo?.branding?.bg_color      || "#080808",
    nav:     companyInfo?.branding?.nav_color     || "#0a0a0a",
    card:    companyInfo?.branding?.card_color    || "#0f0f0f",
    card2:   companyInfo?.branding?.card2_color   || "#1a1a1a",
    border:  companyInfo?.branding?.border_color  || "#1f1f1f",
    text:    companyInfo?.branding?.text_color    || "#ffffff",
    primary: primaryColor,
  };

  return (
    <div className="min-h-screen" data-company-branding style={{ background: bc.bg }}>

      {/* Beheersmodus banner */}
      {isImpersonating && (
        <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-2 text-xs font-bold"
          style={{ background: "#C9A84C", color: "#000" }}>
          <span>Beheersmodus: je beheert {companyInfo?.naam || "dit bedrijf"} als super-admin</span>
          <button onClick={onCloseImpersonate} className="flex items-center gap-1 hover:opacity-70 transition-opacity">
            <X size={13} /> Sluiten
          </button>
        </div>
      )}

      {/* Navbar */}
      <nav className="company-nav border-b border-white/5 px-6 py-4 flex items-center justify-between sticky top-0 z-40"
        style={{ background: bc.nav, borderBottomColor: bc.border }}>
        <div className="flex items-center gap-3">
          {companyInfo?.branding?.logo_url ? (
            <img src={companyInfo.branding.logo_url} alt={companyInfo.naam} className="h-8 w-8 object-cover rounded-lg" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black text-black"
              style={{ background: primaryColor }}>
              {(companyInfo?.naam || "B").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            {companyLoading ? (
              <div className="w-32 h-4 bg-zinc-800 rounded animate-pulse" />
            ) : (
              <span className="font-bold" style={{ color: bc.text }} data-testid="company-naam">
                {companyInfo?.naam || "Bedrijfsportaal"}
              </span>
            )}
            <span className="text-zinc-500 text-xs ml-2">via FitSmith</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Plan badge */}
          {companyInfo?.plan && (
            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold border"
              style={{
                background: currentPlan === "business" ? "#10b98115" : currentPlan === "pro" ? "#C9A84C15" : "#6366f115",
                color: currentPlan === "business" ? "#10b981" : currentPlan === "pro" ? "#C9A84C" : "#6366f1",
                borderColor: currentPlan === "business" ? "#10b98130" : currentPlan === "pro" ? "#C9A84C30" : "#6366f130",
              }}>
              {companyInfo.plan_naam || currentPlan}
            </span>
          )}
          {!isImpersonating && (
            <>
              <span className="text-zinc-600 text-sm hidden sm:inline">{user?.name}</span>
              <button onClick={handleLogout} data-testid="logout-btn"
                className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm transition-colors border border-white/8 px-3 py-1.5 rounded-lg hover:border-white/20"
                style={{ borderColor: bc.border }}>
                <LogOut size={12} /> Uitloggen
              </button>
            </>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs — alleen de toegestane features in de juiste volgorde */}
        <div className="company-tab-bar flex gap-1 border border-white/5 rounded-xl p-1 mb-8 w-fit flex-wrap"
          style={{ background: bc.card, borderColor: bc.border }}>
          {visibleTabs.map(tab => (
            <button key={tab.key} onClick={() => handleTabClick(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.key ? "text-black" : "hover:text-white"
              }`}
              style={activeTab === tab.key
                ? { background: primaryColor, color: "#000" }
                : { color: bc.text + "70" }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "dashboard"    && <DashboardTab companyInfo={companyInfo} token={token} bc={bc} />}
        {activeTab === "activiteit"   && <ActiviteitTab token={token} bc={bc} onSelectSporter={() => setActiveTab("sporters")} />}
        {activeTab === "sporters"     && <SportersTab token={token} planInfo={{ currentPlan, max_sporters: (companyInfo?.sporters_limit || 10) + (companyInfo?.extra_sporters || 0), onUpgrade: () => handleTabClick("abonnement") }} />}
        {activeTab === "rooster"      && <RoosterTab token={token} />}
        {activeTab === "credits"      && <CreditsTab token={token} />}
        {activeTab === "community"    && (
          <CommunityTab
            token={token}
            currentUserId={user?.id || user?._id}
            currentRole={user?.role || "company_admin"}
          />
        )}
        {activeTab === "facturen"     && <BedrijfFacturenTab token={token} />}
        {activeTab === "voeding"      && <VoedingTab token={token} />}
        {activeTab === "intake"       && <IntakeTab token={token} />}
        {activeTab === "personeel"    && <PersoneelTab token={token} />}
        {activeTab === "analytics"    && <AnalyticsTab token={token} primaryColor={primaryColor} />}
        {activeTab === "mappen"       && <MappenTab token={token} />}
        {activeTab === "schemas"      && <SchemasTab token={token} />}
        {activeTab === "bibliotheek"  && <BiblioteekTab token={token} />}
        {activeTab === "templates"    && <TemplatesTab token={token} />}
        {activeTab === "beheerders"   && <AdminsTab token={token} currentUser={user} />}
        {activeTab === "abonnement"   && <AbonnementTab token={token} companyInfo={companyInfo} onUpdate={loadCompany} />}
        {activeTab === "instellingen" && (
          <InstellingenTab token={token} companyInfo={companyInfo} onUpdate={loadCompany} />
        )}
      </div>

      {/* Upgrade Modal */}
      {upgradeModal && (
        <UpgradeModal
          feature={upgradeModal.feature}
          currentPlan={currentPlan}
          requiredPlan={upgradeModal.requiredPlan}
          onClose={() => setUpgradeModal(null)}
          onNavigateAbonnement={() => {
            if (allowedFeatures.includes("abonnement")) {
              setActiveTab("abonnement");
            } else {
              // Bedrijf zonder abonnement-toegang — geef link naar hogere plan
              setActiveTab("dashboard");
            }
          }}
        />
      )}
    </div>
  );
}
